import {
	createContext,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSelector } from "react-redux";

const STORAGE_PREFIX = "reportVisibleColumns";
const EMPTY_ARRAY = [];

export const ReportColumnsContext = createContext(null);

export const REPORT_COLUMN_OPTIONS = {
	dailyAttendance: {
		mandatoryKeys: ["employeeId", "technicianName", "date"],
		defaultHiddenKeys: ["earlyLeaveReason"],
	},
	attendance: {
		mandatoryKeys: ["employeeId", "technicianName"],
	},
	productivity: {
		mandatoryKeys: ["staffName"],
	},
	efficiency: {
		mandatoryKeys: ["taskCode"],
	},
	requests: {
		mandatoryKeys: ["serviceRequestCode"],
	},
	deviceAssignment: {
		mandatoryKeys: ["device_name"],
	},
	shelfAvailability: {
		mandatoryKeys: ["date", "store"],
	},
	shelfMovement: {
		mandatoryKeys: ["client", "date"],
	},
	productExpiration: {
		mandatoryKeys: ["skuName"],
	},
	devicebehavior: {
		mandatoryKeys: ["protocol", "date"],
	},
	vehiclestrips: {
		mandatoryKeys: ["deviceName", "startTime"],
	},
	stop: {
		mandatoryKeys: ["deviceName", "startTime"],
	},
	speed: {
		mandatoryKeys: ["deviceName", "startTime"],
	},
	distance: {
		mandatoryKeys: ["deviceName", "date"],
	},
	fuel: {
		mandatoryKeys: ["deviceName", "startTime"],
	},
	odometer: {
		mandatoryKeys: ["deviceName"],
	},
	maintenance: {
		mandatoryKeys: ["maintenanceName"],
	},
};

export function getColumnKey(col) {
	if (col?.key != null && col.key !== "") return String(col.key);
	if (typeof col?.dataIndex === "string") return col.dataIndex;
	if (Array.isArray(col?.dataIndex)) return col.dataIndex.join(".");
	return "";
}

function loadVisibleKeys(storageKey, allKeys, defaultVisible, mandatoryKeys) {
	try {
		const stored = JSON.parse(localStorage.getItem(storageKey));
		if (Array.isArray(stored)) {
			const valid = stored.filter((key) => allKeys.includes(key));
			return [...new Set([...valid, ...mandatoryKeys])];
		}
	} catch {
		// ignore malformed storage value
	}
	return [...new Set([...defaultVisible, ...mandatoryKeys])];
}

function sameDefs(a, b) {
	return (
		a.length === b.length &&
		a.every(
			(def, index) =>
				def.key === b[index].key && def.title === b[index].title,
		)
	);
}

export function ReportColumnsProvider({ reportKey, children }) {
	const userId = useSelector((state) => state.users.user?.id);
	const options = REPORT_COLUMN_OPTIONS[reportKey] || {};
	const mandatoryKeys = options.mandatoryKeys || EMPTY_ARRAY;
	const defaultHiddenKeys = options.defaultHiddenKeys || EMPTY_ARRAY;
	const storageKey = `${STORAGE_PREFIX}:${userId || "anon"}:${reportKey}`;

	const [defs, setDefs] = useState(EMPTY_ARRAY);
	const [visibleKeys, setVisibleKeysState] = useState(EMPTY_ARRAY);
	const appliedStorageKey = useRef(null);
	const knownKeysRef = useRef(EMPTY_ARRAY);

	const register = useCallback((nextDefs) => {
		setDefs((prev) => (sameDefs(prev, nextDefs) ? prev : nextDefs));
	}, []);

	useLayoutEffect(() => {
		const allKeys = defs.map((def) => def.key).filter(Boolean);
		if (!allKeys.length) return;

		const defaultVisible = allKeys.filter(
			(key) => !defaultHiddenKeys.includes(key),
		);

		if (appliedStorageKey.current !== storageKey) {
			appliedStorageKey.current = storageKey;
			knownKeysRef.current = allKeys;
			setVisibleKeysState(
				loadVisibleKeys(storageKey, allKeys, defaultVisible, mandatoryKeys),
			);
			return;
		}

		const newlyAdded = allKeys.filter(
			(key) => !knownKeysRef.current.includes(key),
		);
		knownKeysRef.current = allKeys;

		setVisibleKeysState((prev) => {
			const known = prev.filter((key) => allKeys.includes(key));
			const added = newlyAdded.filter((key) => defaultVisible.includes(key));
			const next = [...new Set([...known, ...added, ...mandatoryKeys])];
			if (prev.length === next.length && prev.every((key, i) => key === next[i])) {
				return prev;
			}
			return next;
		});
	}, [defs, storageKey, defaultHiddenKeys, mandatoryKeys]);

	const setVisibleKeys = useCallback(
		(nextKeys) => {
			const allKeys = defs.map((def) => def.key);
			const merged = [
				...new Set([
					...nextKeys.filter((key) => allKeys.includes(key)),
					...mandatoryKeys,
				]),
			];
			setVisibleKeysState(merged);
			localStorage.setItem(storageKey, JSON.stringify(merged));
		},
		[defs, mandatoryKeys, storageKey],
	);

	const reset = useCallback(() => {
		const allKeys = defs.map((def) => def.key);
		const defaultVisible = allKeys.filter(
			(key) => !defaultHiddenKeys.includes(key),
		);
		const next = [...new Set([...defaultVisible, ...mandatoryKeys])];
		setVisibleKeysState(next);
		localStorage.setItem(storageKey, JSON.stringify(next));
	}, [defs, defaultHiddenKeys, mandatoryKeys, storageKey]);

	const value = useMemo(
		() => ({
			defs,
			register,
			visibleKeys,
			setVisibleKeys,
			reset,
			mandatoryKeys,
		}),
		[defs, register, visibleKeys, setVisibleKeys, reset, mandatoryKeys],
	);

	return (
		<ReportColumnsContext.Provider value={value}>
			{children}
		</ReportColumnsContext.Provider>
	);
}

export function useReportColumnsContext() {
	return useContext(ReportColumnsContext);
}

export function useReportTableColumns(columns) {
	const ctx = useContext(ReportColumnsContext);
	const register = ctx?.register;
	const defsRef = useRef(EMPTY_ARRAY);
	const defs = useMemo(() => {
		const next = columns
			.map((col) => ({
				key: getColumnKey(col),
				title: typeof col.title === "string" ? col.title : getColumnKey(col),
			}))
			.filter((def) => def.key);
		if (sameDefs(defsRef.current, next)) return defsRef.current;
		defsRef.current = next;
		return next;
	}, [columns]);

	useLayoutEffect(() => {
		if (!register) return;
		register(defs);
	}, [register, defs]);

	if (!ctx?.visibleKeys?.length) return columns;

	const allowed = new Set([
		...ctx.visibleKeys,
		...(ctx.mandatoryKeys || []),
	]);

	return columns.filter((col) => allowed.has(getColumnKey(col)));
}
